import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { openConfirmationModal } from '../reducers/uiSlice';
import { confirmationService } from '../services/confirmationService';
import { toastService } from '../services/toastService';
import { db } from '../lib/database';
import { submissionService } from '../services/submissionService';
import { formsService } from '../services/formsService';
import { fileUploadService } from '../services/fileUploadService';

const buildFormFromSubmission = submission => {
  const answers = submission.answers || [];
  const sectionsMap = new Map();
  const defaultSectionId = 'default';

  answers.forEach(answer => {
    const field = answer.field;
    if (!field) return;

    const sectionId = field.form_section_id || field.section?.id || defaultSectionId;

    if (!sectionsMap.has(sectionId)) {
      sectionsMap.set(sectionId, {
        id: sectionId,
        name: field.section?.name || field.form_section_name || 'Form Section',
        label: field.section?.label || field.form_section_label,
        order: field.section?.order || 0,
        fields: []
      });
    }

    const fieldWithAnswer = {
      ...field,
      answer: answer.value_JSON || answer.value_json || answer.value,
      answerData: answer
    };

    sectionsMap.get(sectionId).fields.push(fieldWithAnswer);
  });

  const sections = Array.from(sectionsMap.values());
  sections.sort((a, b) => (a.order || 0) - (b.order || 0));
  sections.forEach(section => {
    section.fields.sort((a, b) => (a.order || 0) - (b.order || 0));
  });

  return {
    id: submission.form?.id || submission.form_id,
    title: submission.form?.name || 'Submission Details',
    description: submission.form?.description || '',
    sections: sections
  };
};

export const useFormDataView = id => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form, setForm] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setError('No submission ID provided.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const isTempId = id.startsWith('temp_');
    const isOffline = !navigator.onLine || isTempId;

    submissionService
      .getSubmissionById(id, isOffline)
      .then(sRes => {
        // Handle common API error wrappers
        if (sRes.data && sRes.data.status === false) {
          throw new Error(sRes.data.message || 'You do not have permission to view this submission.');
        }

        const responseData = sRes.data?.data;
        const sub = responseData?.data || responseData || sRes.data;

        if (!sub) throw new Error('Submission not found.');

        // Handle cases where API returns data but indicates no access
        if (sub.can_view === false) {
          throw new Error(sRes.data.message || 'You do not have permission to view this submission.');
        }

        setSubmission(sub);

        const hasEmbeddedDefinitions = sub.answers && Array.isArray(sub.answers) && sub.answers.length > 0 && !!sub.answers[0].field;

        if (hasEmbeddedDefinitions && !isOffline) {
          const constructedForm = buildFormFromSubmission(sub);
          setForm(constructedForm);
          return null;
        } else {
          // If no form_id, we can't fetch the form structure
          if (!sub.form_id) {
            throw new Error('Could not identify the form for this submission.');
          }

          return formsService.getById(sub.form_id, isOffline).then(fRes => {
            const fResponseData = fRes.data.data;
            const formData = fResponseData?.data || fResponseData || fRes.data;
            if (!formData || !formData.sections) throw new Error('Form structure could not be loaded.');

            const filePromise = isOffline ? fileUploadService.getStoredFiles(id) : Promise.resolve({});
            const responsePromise = isOffline ? db.form_responses.where('submission_id').equals(id).toArray() : Promise.resolve([]);

            return Promise.all([filePromise, responsePromise]).then(([storedFiles, offlineResponses]) => {
              let answersMap = {};
              if (isOffline) {
                if (offlineResponses.length > 0) {
                  answersMap = offlineResponses.reduce((acc, resp) => {
                    acc[resp.question_id] = resp.value;
                    return acc;
                  }, {});
                } else {
                  answersMap = sub.answers || {};
                }
              } else {
                const answersArray = Array.isArray(sub.answers) ? sub.answers : [];
                answersMap = answersArray.reduce((acc, a) => {
                  acc[a.form_field_id] = a;
                  return acc;
                }, {});
              }

              const sectionsWithAnswers = formData.sections.map(section => ({
                ...section,
                fields: (section.fields || []).map(question => {
                  let answerValue = null;
                  const fieldType = question.type?.toUpperCase();

                  if (isOffline) {
                    answerValue = answersMap[question.id] || answersMap[question.name];
                    if (['FILE', 'IMAGE', 'MULTI-FILE', 'SIGNATURE'].includes(fieldType)) {
                      const fieldFiles = storedFiles[question.id] || [];
                      if (fieldFiles.length > 0) answerValue = fieldFiles;
                    }
                  } else {
                    const answerObj = answersMap[question.id];
                    if (answerObj) {
                      const valJson = answerObj.value_JSON || answerObj.value_json;
                      if (valJson) {
                        try {
                          answerValue = JSON.parse(valJson);
                        } catch {
                          answerValue = valJson;
                        }
                      } else {
                        answerValue = answerObj.value;
                      }
                    }
                  }
                  return { ...question, answer: answerValue };
                })
              }));

              setForm({
                title: formData.title || formData.name,
                description: formData.description,
                sections: sectionsWithAnswers
              });
            });
          });
        }
      })
      .catch(e => {
        console.error('View Error:', e);
        setSubmission(null);
        setForm(null);
        const errMsg = e.response?.data?.message || e.message || 'Failed to load submission data.';
        setError(errMsg);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleDelete = () => {
    const confirmId = `delete-${id}`;
    confirmationService.register(confirmId, () => {
      setIsDeleting(true);
      return submissionService
        .deleteSubmission(submission.id)
        .then(() => {
          toastService.success('Submission successfully deleted.');
          navigate(`/forms/data?formId=${submission.form_id}`, { replace: true });
        })
        .catch(() => toastService.error('Failed to delete submission.'))
        .finally(() => setIsDeleting(false));
    });

    dispatch(
      openConfirmationModal({
        title: 'Delete Submission',
        message: 'Are you sure? This action cannot be undone.',
        type: 'destructive',
        confirmId
      })
    );
  };

  // True if this submission lives only locally (offline/pending) — governs which actions are available
  const isOfflineSubmission = !!(submission && (
    String(submission.id || '').startsWith('temp_') ||
    submission.syncStatus === 'pending'
  ));

  return { form, submission, loading, isDeleting, error, handleDelete, isOfflineSubmission };
};
